import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Clock, User } from 'lucide-react';
import OrnateDivider from './OrnateDivider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlaceholderCard = ({ idx }) => (
  <div
    key={`ph-${idx}`}
    className="min-w-[280px] max-w-[320px] sm:min-w-[320px] lg:min-w-[360px] flex-shrink-0 rounded-2xl overflow-hidden border border-primary/20 bg-primary/5"
    data-testid={`latest-placeholder-${idx}`}
  >
    <div className="aspect-[16/9] bg-gradient-to-br from-primary/25 via-primary/10 to-transparent" />
    <div className="p-5">
      <div className="text-[10px] uppercase tracking-[0.3em] text-primary/50 mb-2">Soon</div>
      <h3 className="font-heading text-lg golden-text mb-1">Coming Soon</h3>
      <p className="text-sm text-primary/60 italic">The first writings are on their way.</p>
    </div>
  </div>
);

const PostCard = ({ post, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="min-w-[280px] max-w-[320px] sm:min-w-[320px] lg:min-w-[360px] flex-shrink-0 rounded-2xl overflow-hidden border border-primary/20 bg-primary/5 hover:border-primary/50 transition text-left group"
    data-testid={`latest-post-card-${post.id}`}
  >
    <div className="aspect-[16/9] bg-primary/5 overflow-hidden relative">
      {post.cover_image_url ? (
        <img
          src={post.cover_image_url}
          alt=""
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-primary/25 via-primary/10 to-transparent" />
      )}
      {post.category && (
        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.25em] bg-black/60 text-primary border border-primary/40 backdrop-blur-sm">
          {post.category}
        </span>
      )}
    </div>
    <div className="p-5">
      <h3 className="font-heading text-lg text-primary line-clamp-2 mb-1">{post.title}</h3>
      {post.subtitle && (
        <p className="text-sm text-primary/70 italic line-clamp-2 mb-3">{post.subtitle}</p>
      )}
      <div className="flex items-center gap-3 text-[11px] text-primary/60">
        <span className="inline-flex items-center gap-1"><User size={12} />{post.author_name}</span>
        <span>·</span>
        <span>{post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</span>
        <span>·</span>
        <span className="inline-flex items-center gap-1"><Clock size={12} />{post.read_time || 1} min</span>
      </div>
    </div>
  </button>
);

const LatestFromThePath = () => {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const scrollerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/blog/homepage`)
      .then((r) => setPosts(r.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoaded(true));
  }, []);

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.9), behavior: 'smooth' });
  };

  const items = posts.length > 0
    ? posts
    : [0, 1, 2]; // placeholders when empty

  return (
    <section className="relative py-20 overflow-hidden" data-testid="latest-from-path-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <OrnateDivider variant="lotus" className="mb-6 max-w-md mx-auto" />
          <h2 className="text-3xl sm:text-4xl font-heading font-bold golden-text text-glow tracking-wider">
            Latest From the Path
          </h2>
          <p className="mt-3 text-sm italic text-primary/55">
            Writings from the Siddha Samaj tradition
          </p>
        </div>

        <div className="relative">
          {/* Desktop arrows */}
          <button
            type="button"
            onClick={() => scroll(-1)}
            className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full border border-primary/30 bg-background/80 text-primary hover:bg-primary/10 backdrop-blur"
            aria-label="Scroll left"
            data-testid="latest-scroll-left"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => scroll(1)}
            className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full border border-primary/30 bg-background/80 text-primary hover:bg-primary/10 backdrop-blur"
            aria-label="Scroll right"
            data-testid="latest-scroll-right"
          >
            <ChevronRight size={18} />
          </button>

          <div
            ref={scrollerRef}
            className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 scrollbar-none"
            style={{ scrollbarWidth: 'none' }}
          >
            {loaded && items.map((p, i) => (
              typeof p === 'object'
                ? <div key={p.id} className="snap-start"><PostCard post={p} onClick={() => navigate(`/blog/${p.slug}`)} /></div>
                : <div key={`ph-${i}`} className="snap-start"><PlaceholderCard idx={i} /></div>
            ))}
          </div>
        </div>

        <div className="text-center mt-8">
          <Link
            to="/blog"
            className="inline-block text-sm font-heading uppercase tracking-[0.25em] text-primary hover:text-primary/80 transition"
            data-testid="latest-explore-all"
          >
            Explore All Writings →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LatestFromThePath;
